import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

export type Language = 'ru' | 'en'

export type Theme = 'light' | 'dark' | 'auto'

export type UnitSystem = 'SI' | 'BIS'

export const DEFAULT_LANGUAGE: Language = 'en'

interface SettingsState {
	language: Language,
	theme: Theme,
	unitSystem: UnitSystem,
	showHints: boolean,
	emailNotifications: boolean,
	autoSaveDrafts: boolean,
}

const initialState: SettingsState = {
	language: DEFAULT_LANGUAGE,
	theme: 'light',
	unitSystem: 'SI',
	showHints: true,
	emailNotifications: true,
	autoSaveDrafts: false,
}

const settingsSlice = createSlice({
	name: 'settings',
	initialState,
	selectors: {
		getLanguage: (state) => state.language,
		getTheme: (state) => state.theme,
		getUnitSystem: (state) => state.unitSystem,
		getShowHints: (state) => state.showHints,
		getEmailNotifications: (state) => state.emailNotifications,
		getAutoSaveDrafts: (state) => state.autoSaveDrafts,
	},
	reducers: {
		setLanguage: (state, action: PayloadAction<Language>) => {
			state.language = action.payload
		},
		setTheme: (state, action: PayloadAction<Theme>) => {
			state.theme = action.payload
		},
		setUnitSystem: (state, action: PayloadAction<UnitSystem>) => {
			state.unitSystem = action.payload
		},
		setShowHints: (state, action: PayloadAction<boolean>) => {
			state.showHints = action.payload
		},
		setEmailNotifications: (state, action: PayloadAction<boolean>) => {
			state.emailNotifications = action.payload
		},
		setAutoSaveDrafts: (state, action: PayloadAction<boolean>) => {
			state.autoSaveDrafts = action.payload
		},
		toggleUnitSystem: (state) => {
			state.unitSystem = state.unitSystem === 'SI' ? 'BIS' : 'SI'
		},
		resetSettings: () => initialState,
	},
})

export const {
	setLanguage,
	setTheme,
	setUnitSystem,
	setShowHints,
	setEmailNotifications,
	setAutoSaveDrafts,
	toggleUnitSystem,
	resetSettings,
} = settingsSlice.actions

export const {
	getLanguage,
	getTheme,
	getUnitSystem,
	getShowHints,
	getEmailNotifications,
	getAutoSaveDrafts,
} = settingsSlice.selectors

export default settingsSlice